import React, { useEffect, useRef, useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import './styles.css';
import {
  createTheme,
  CssBaseline,
  Fade,
  PaletteMode,
  responsiveFontSizes,
  Snackbar,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Room } from './components/room';
import { Header } from './components/header';
import { Auth } from './components/auth';
import { io, Socket } from 'socket.io-client';
import { fetchGet, fetchPost } from './api/common';
import { socketGlobalListeners } from './sockets/listeners';
import { InaccuraciesReport } from './components/inaccuracies-report';
import {
  ChatMessage,
  DailyData,
  GameDataFromServer,
  Mode,
  Player,
  SocketCallback,
  WhosOnline,
  DailiesAuthor,
} from './components/room/types';
import { Admin } from './components/admin';
import { PoolPlay } from './components/pool-play';
import { SocketCallbackHandler } from './services/socket-callback';
import { Main } from './components/main';
import { Leaderboards } from './components/leaderboards';
import { QuestionsSetup } from './components/questions-setup';
import { Lobby } from './components/main/Lobby';
import { Panels } from './components/play-menu';
import { ConfirmReadyModal } from './modals/ConfirmReadyModal';
import { track } from './api/track';
import { Stager } from './components/stager';
import { Stats } from './components/stats';
import { isMobile } from 'react-device-detect';
import { LaunchPracticeGameModal } from './modals/LaunchPracticeGameModal';
import {
  createGame,
  joinGame,
  playGhostChallenge,
  rejoinGame,
  spectateGame,
} from './services/game';
import { CustomMessage, CustomMessageModal } from './modals/CustomMessageModal';
import { ErrorMessageModal } from './modals/ErrorMessageModal';
import { useIdleTimer } from 'react-idle-timer';
import { debounce } from 'lodash';
import { muiCustomComponent } from './common/mui-components';
import { palette, paletteLight } from './common/mui-palette';
import { Dailies } from './components/dailies';
import { Flashcard, Flashcards } from './components/flashcards';
import { FlashcardMode } from './components/flashcards/FlashcardMode';
import { Help } from './components/help';
import {
  PlayDailyConfirmationDialog,
  PlayDailyConfirm,
} from './dialog/PlayDailyConfirmationDialog';
import {
  GhostChallengConfig,
  GhostChallengeDialog,
} from './dialog/GhostChallengeDialog';
import {
  GhostChallenge,
  GhostChallengesViewDialog,
} from './dialog/GhostChallengesViewDialog';
import { MobileDisclaimer } from './dialog/MobileDisclaimer';
import { JoyrideComponent } from './components/joyride';
import { CategoryBansDialog } from './dialog/CategoryBansDialog';
import { SkillAssessmentDialog } from './dialog/SkillAssessmentDialog';
import { TournamentLobby } from './components/tournament/TournamentLobby';
import { RecommendPracticeGameDialog } from './dialog/RecommendPracticeGameDialog';
import { RefreshBrowserDialog } from './dialog/RefreshBrowserDialog';
import {
  socketPath,
} from '@par/shared/common/sockets';
import {
  devDockerLoopbackGameSocket,
  devDockerLoopbackSpectateSocket,
} from './config/devDockerLoopback';
import { CreateGameConfig, Game, Teams } from '@par/shared/types/game';
import { ScoreScreenPublic } from './components/public/ScoreScreenPublic';
import { socketRemoveGlobalListeners } from './sockets/listeners';
import { TournamentSignUpDialog } from './dialog/TournamentSignUpDialog';
import { ViewSearchPoolDialog } from './dialog/ViewSearchPoolDialog';
import { CustomQuestions } from './components/custom-questions';
import { UserSubmittedQuestions } from './components/user-submitted-questions';
import { PricingPage } from './components/payment';
import { HandicapWinratesDialog } from './dialog/HandicapWinratesDialog';
import { Unsubscribe } from './components/auth/Unsubscribe';
import { BottomNav } from './components/bottom-nav';
import {
  DemonsData,
  FaceYourDemonsDialog,
} from './dialog/FaceYourDemonsDialog';
import { GameLoading } from './components/room/GameLoading';
import { DetailedUserAchievement, Profile } from '@par/shared/types/player';
import { StarterKits } from './components/starter-kits';
import {
  addTabId,
  handleStorageChange,
  removeTabId,
} from './services/multiple-tabs/helper';
import { PlayMenu2 } from './components/play-menu2';
import { TeamPickingDialog } from './dialog/TeamPickingDialog';
import { MobileBrowserDailyData } from '@par/shared/types/mobile-daily';
import { LeagueLobby } from './components/league/LeagueLobby';
import { League } from './components/league';
import { LeagueStandings } from './components/league/LeagueStandings';
import { LeagueAdmin } from './components/league/LeagueAdmin';
import { LeagueTeams } from './components/league/LeagueTeams';
import { LeaguePlayers } from './components/league/LeaguePlayers';
import { LeagueRules } from './components/league/LeagueRules';
import { LeagueHowToJoin } from './components/league/LeagueHowToJoin';
import { ProfileMain } from './components/profile/ProfileMain';
import { ProfileAchievements } from './components/profile/ProfileAchievements';
import { SoloPunchcard } from './components/profile/SoloPunchcard';
import { ProfileAdvancedStats } from './components/profile/ProfileAdvancedStats';
import { CQDashboard } from './components/profile/custom-questions/CQDashboard';
import { ProfileSettings } from './components/profile/ProfileSettings';
import { ProfileAccount } from './components/profile/ProfileAccount';
import { ConfirmLogoutDialog } from './dialog/ConfirmLogoutDialog';
import { ReplayPage } from './components/replay/ReplayPage';

export const ColorModeContext = React.createContext({
  toggleColorMode: () => {},
});

let allChatPreviewTimeout: any;

function App() {
  const tabId = Math.random().toString(36).substr(2, 9);
  const location = useLocation();
  const navigate = useNavigate();
  const [socket, setSocket] = useState<Socket>();
  const [openRefreshModal, setOpenRefreshModal] = useState(false);
  const [whosOnline, setWhosOnline] = useState<WhosOnline[]>();
  const [playersOnlineCount, setPlayersOnlineCount] = useState<number>();
  const [isAdmin, setIsAdmin] = useState(false);
  const [profile, setProfile] = useState<Profile>();
  const [gameId, setGameId] = useState<string>();
  const [socketCallbackResponse, setSocketCallbackResponse] =
    useState<SocketCallback>();
  const [openPlayMenu, setOpenPlayMenu] = useState(false);
  const [selectedAccordion, setSelectedAccordion] = useState<false | Panels>(
    'solo'
  );
  const [createGameConfig, setCreateGameConfig] = useState<CreateGameConfig>();
  const [onlinePlayer, setOnlinePlayer] = useState<
    WhosOnline & { currentGameInfo?: CreateGameConfig }
  >();
  const [openConfirmReadyModal, setOpenConfirmReadyModal] = useState<{
    open: boolean;
    username?: string;
    delayedClose?: boolean;
    confirmReadyExpiresAt?: number;
    autoStartIn?: number;
  }>({ open: false });
  const [confirmedReady, setConfirmedReady] = useState(false);
  const [playersReady, setPlayersReady] = useState<Record<string, Player>>();
  const [allPlayersReady, setAllPlayersReady] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState<{
    open: boolean;
    message: string;
  }>();
  // to save a trip to the server, we will track the demos played on the client side
  // and only go to the server if user refreshes
  const [demoCount, setDemoCount] = useState(0);
  const [disconnected, setDisconnected] = useState(false);
  const [allLobbyGames, setAllLobbyGames] = useState<Game[]>();
  const [lobbyGames, setLobbyGames] = useState<Game[]>();
  const [lobbyGamesHistory, setLobbyGamesHistory] = useState<Game[]>();
  const [lobbyOpenGames, setLobbyOpenGames] = useState<Game[]>();
  const [leagueLobbyGames, setLeagueLobbyGames] = useState<Game[]>();
  const [leagueLobbyGamesHistory, setLeagueLobbyGamesHistory] =
    useState<Game[]>();
  const [tournamentLobbyGames, setTournamentLobbyGames] = useState<Game[]>();
  const [tourneyLobbyGamesHistory, setTourneyLobbyGamesHistory] =
    useState<Game[]>();
  const [maintenance, setMaintenance] = useState({
    scheduled: false,
    bannerMessage: '',
  });
  const [bannerMessage, setBannerMessage] = useState<string>();
  const [showMultipleTabsBanner, setShowMultipleTabsBanner] = useState(false);
  const [clientVersion, setClientVersion] = useState<number>();
  const [latestClientVersion, setLatestClientVersion] = useState<number>();
  const [openLaunchPracticeGameModal, setOpenLaunchPracticeGameModal] =
    useState(false);
  const [openRecommendPractice, setOpenRecommendPractice] = useState(false);
  const [allChat, setAllChat] = useState<ChatMessage[]>();
  const [generalChatLastSeen, setAllChatLastSeen] = useState<number>();
  const [unreadAllChat, setUnreadAllChat] = useState<boolean>();
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorMessageModal, setOpenErrorMessageModal] = useState(false);
  const [customMessage, setCustomMessage] = useState<CustomMessage>({
    openModal: false,
  });
  const [mode, setMode] = React.useState<PaletteMode>('dark');
  const [dailyData, setDailyData] = useState<DailyData>();
  const [dailiesAuthor, setDailiesAuthor] = useState<DailiesAuthor>();
  const [lastDailySetPlayedCache, setLastDailySetPlayedCache] = useState(0);
  const [flashcards, setFlashcards] = useState<Flashcard[]>();
  const [openAllChat, setOpenAllChat] = useState(false);
  const [showAllChatPreview, setShowAllChatPreview] = useState(false);
  const [openPlayDailyConfirmation, setOpenPlayDailyConfirmation] =
    useState<PlayDailyConfirm>({ open: false });
  const [openGhostChallenge, setOpenGhostChallenge] = useState(false);
  const [ghostChallengeTo, setGhostChallengeTo] = useState<{
    profileId: string;
    username: string;
    ghostChallengeStatus: string;
  }>();
  const [openGhostChallengesView, setOpenGhostChallengesView] = useState(false);
  const [incomingGhostChallenges, setIncomingGhostChallenges] =
    useState<number>();
  const [openMobileDisclaimer, setOpenMobileDisclaimer] = useState(false);
  const [openSkillAssessment, setOpenSkillAssessment] = useState(false);
  const [openTourneySignUp, setOpenTourneySignUp] = useState(false);
  const [openCategoryBans, setOpenCategoryBans] = useState(false);
  const [bannedCategories, setBannedCategories] = useState<string[]>();
  const [gameDataFromServer, setGameDataFromServer] =
    useState<GameDataFromServer>();
  const [startedGameDataFromServer, setStartedGameDataFromServer] =
    useState<GameDataFromServer>();
  const [spectateGameDataFromServer, setSpectateGameDataFromServer] =
    useState<GameDataFromServer>();
  const [spectateSocket, setSpectateSocket] = useState<Socket | null>(null);
  const [openSearchPoolDialog, setOpenSearchPoolDialog] = useState(false);
  const [openHandicapWinrates, setOpenHandicapWinrates] = useState(false);
  const [openFaceYourDemons, setOpenFaceYourDemons] = useState(false);
  const [demonsData, setDemonsData] = useState<DemonsData>();
  const [detailedUserAchievements, setDetailedUserAchievements] =
    useState<DetailedUserAchievement[]>();
  const [whosOnlineUpdateSubscribed, setWhosOnlineUpdateSubscribed] =
    useState(false);
  const [openTeamPickingDialog, setOpenTeamPickingDialog] = useState(false);
  const [teamPickingStatus, setTeamPickingStatus] = useState<{
    host?: string;
    teams: Teams;
    players: Record<string, Player>;
    league?: boolean;
    leagueSeason?: number;
  }>();
  const [enabledTeams, setEnabledTeams] = useState(false);
  const [mobileDailyData, setMobileDailyData] =
    useState<MobileBrowserDailyData>();
  const [openConfirmLogoutDialog, setOpenConfirmLogoutDialog] = useState(false);
  const dailyThemeRef = useRef<string>(); // weird place to do this, but avoid nasty prop drilling
  const selfWhosOnlineRef = useRef<WhosOnline>();
  const logoutClickedRef = useRef(false);

  // temporary testing bottom nav
  const [testingFeature, setTestingFeature] = useState(false);

  // temp tracking
  const usernameRef = useRef<string>();

  useEffect(() => {
    if (profile) {
      navigate(location.pathname);
      return;
    }
    const authenticate = async () => {
      if (location.pathname.includes('unsubscribe')) {
        return;
      }
      try {
        const res = await fetchGet('/profile');
        // console.log('Fetch Profile', res);
        setProfile(res as Profile);
      } catch {
        if (
          location.pathname.includes('forgot-password') ||
          location.pathname.includes('reset-password')
        ) {
          return;
        }
        navigate('/login');
      }
    };

    if (location.pathname.includes('game/view')) {
      return;
    }
    authenticate();
    const openTabsCount = addTabId(tabId);
    if (openTabsCount > 1) {
      setShowMultipleTabsBanner(true);
    } else {
      setShowMultipleTabsBanner(false);
    }
    window.addEventListener('focus', onTabFocus);
    window.addEventListener('blur', onTabBlur);
    window.addEventListener('beforeunload', () => removeTabId(tabId));
    window.addEventListener('storage', handleStorageChange);
    onTabFocus();
    return () => {
      window.removeEventListener('focus', onTabFocus);
      window.removeEventListener('blur', onTabBlur);
      window.removeEventListener('beforeunload', onTabBlur);
      window.removeEventListener('storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: tab tracking + listeners; adding profile/location would duplicate setup
  }, []);

  useEffect(() => {
    if (profile && (!socket || !socket.connected)) {
      usernameRef.current = profile.username;
      setIsAdmin(profile.admin);
      const _socket = io(devDockerLoopbackGameSocket(process.env.REACT_APP_SOCKET), {
        path: socketPath,
        transports: ['websocket'],
        withCredentials: true,
      });
      _socket.on('connect', () => {
        setSocket(_socket);
        connectProfileToSocket(_socket, location.pathname, profile, isMobile);
        setDisconnected(false);
      });
      _socket.on('connect_error', () => {
        setDisconnected(true);
      });
      checkMaintenanceStatus();
      fetchClientVersion();
      fetchGhostChallengesReceived(profile.id);
      setDemoCount(profile.demoSetsSeen?.length || 0);
      logoutClickedRef.current = false;
      fetchHistoricalGames(100);
      fetchHistoricalTourneyGames(7);
      fetchHistoricalLeagueGames();
      fetchDailyData();
      fetchUserAchievements();
      fetchDemons();
      fetchMobileDailyData();
      if (!profile.skillAssessmentSelection) {
        setOpenSkillAssessment(true);
        if (isMobile) {
          updateFirstLoginMobile();
        }
      }
      // if (!profile.tournamentSignUp) {
      //   setOpenTourneySignUp(true);
      // }
    }
    if (!profile && socket) {
      socket.disconnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- profile gate only; fetch* helpers are not useCallback-stable
  }, [profile]);

  const updateFirstLoginMobile = async () => {
    const res = await fetchPost('/profile/first-login-mobile', {
      profileId: profile.id,
    });
  };

  useEffect(() => {
    if (!socket || !profile) return;
    socket.on('disconnect', (data: any) => {
      setDisconnected(true);
      if (!selfWhosOnlineRef?.current) return;
      // sockets are set to reconnect automatically but there are a
      // few cases where we do not want that to happen because a lot of development is occurring
      // and its convenient to have the user just refresh the app when they inevitably disconnect
      // - if a user is idle, do not reconnect them since they are not active and wont mind refreshing
      // - if a user is in an offline state but the socket is connected for some reason, disconnect fully
      if (
        selfWhosOnlineRef?.current?.isIdle ||
        !selfWhosOnlineRef?.current?.online ||
        (!selfWhosOnlineRef?.current?.gameInProgress &&
          !selfWhosOnlineRef?.current?.spectateInProgress)
      ) {
        console.log('FULL DC!', selfWhosOnlineRef.current);
        socket.disconnect();
        setSocket(undefined);
        if (!logoutClickedRef.current) {
          setOpenRefreshModal(true);
        }
      }
    });
    socketGlobalListeners(
      changeGameMode,
      handleReadyCancelByOtherPlayer,
      lobbyGameUpdate,
      lobbyGamePropertyUpdate,
      lobbyPlayerUpdate,
      profile.id,
      promptReadyCheck,
      setAllPlayersReady,
      reverseAllChat,
      setAllLobbyGames,
      setMaintenance,
      setBannerMessage,
      setLatestClientVersion,
      updatePlayerIsReady,
      setGameDataFromServer,
      setOnlinePlayer,
      setWhosOnline,
      setPlayersOnlineCount,
      setTeamPickingStatus,
      setEnabledTeams,
      updateWhosOnline,
      socket
    );
    return () => {
      socketRemoveGlobalListeners(socket);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- socket instance; listing handler deps would re-bind listeners every render
  }, [socket]);

  useEffect(() => {
    if (!socket || !profile) return;
    socket.emit('getOnlinePlayer', { profileId: profile.id });
    fetchGhostChallengesReceived(profile.id);

    // This logic will prevent the WhosOnline list from running
    // in the background while a user is in a game (rooom).
    // Helps to optimize this as users sign on/off and other statuses change
    // if (location.pathname === '/room') {
    //   setWhosOnlineUpdateSubscribed(false);
    //   socket.off('whosOnlineUpdate');
    // } else {
    //   if (whosOnlineUpdateSubscribed) return;
    //   setWhosOnlineUpdateSubscribed(true);
    //   socket.on('whosOnlineUpdate', updateWhosOnline);
    //   socket.emit('requestLoggedInUsers', profile.id);
    // }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- emit uses profile.id; full `profile` would re-run on unrelated profile updates
  }, [location.pathname, profile?.id, socket]);

  useEffect(() => {
    if (!allPlayersReady || !gameDataFromServer) return;
    // console.log('gameDataFromServer', gameDataFromServer);
    setStartedGameDataFromServer((prev) => ({
      ...prev,
      ...gameDataFromServer,
    }));
    setAllPlayersReady(false);
    setOpenTeamPickingDialog(false);
    setOpenConfirmReadyModal({
      open: false,
      username: '',
    });
    setConfirmedReady(false);
    navigate('/loading');
  }, [allPlayersReady, gameDataFromServer, navigate]);

  const connectProfileToSocket = (
    socket: Socket,
    path: string,
    profile: Profile,
    isMobile: boolean
  ) => {
    socket.emit(
      'connectProfile',
      {
        profile: profile,
        clientSocketId: socket.id,
        isMobile: isMobile,
      },
      (res: SocketCallback) => {
        console.log('connectProfile', res.data);
        socket.emit('getOnlinePlayer', { profileId: profile.id });
      }
    );
    // navigate(path);
  };

  useEffect(() => {
    if (!allChat) return;
    const recentMessageCreatedAt = allChat[allChat.length - 1]?.createdAt || 0;
    if (
      recentMessageCreatedAt > (generalChatLastSeen || 0) &&
      recentMessageCreatedAt > (profile?.lastLogin || 0) &&
      !openAllChat
    ) {
      setUnreadAllChat(true);
      if (allChat && !openAllChat) {
        clearTimeout(allChatPreviewTimeout);
        setShowAllChatPreview(true);
      }
    }
    allChatPreviewTimeout = setTimeout(() => {
      setShowAllChatPreview(false);
    }, 4000);
  }, [allChat, generalChatLastSeen, openAllChat, profile?.lastLogin]);

  useEffect(() => {
    if (openAllChat && allChat) {
      clearTimeout(allChatPreviewTimeout);
      setUnreadAllChat(false);
      setAllChatLastSeen(allChat[allChat.length - 1]?.createdAt);
    }
  }, [openAllChat, allChat]);

  useEffect(() => {
    if (!allLobbyGames) return;
    setLobbyGames(allLobbyGames.filter((g) => !g.tournament && !g.league));
    setTournamentLobbyGames(allLobbyGames.filter((g) => g.tournament));
    setLeagueLobbyGames(allLobbyGames.filter((g) => g.league));
    setLobbyOpenGames(
      allLobbyGames.filter(
        (g) => !g.league && !g.tournament && !g.gameStarted && g.mode !== 'solo'
      )
    );
  }, [allLobbyGames]);

  useEffect(() => {
    if (!onlinePlayer) return;
    if (onlinePlayer.currentGameId) {
      setGameId(onlinePlayer.currentGameId);
    }
  }, [onlinePlayer]);

  // Avoid disconnect on first socket `latestClientVersion` before `fetchClientVersion` sets `clientVersion`
  // (`number !== undefined` was true and killed the socket immediately → Play stayed disabled).
  useEffect(() => {
    if (latestClientVersion === undefined || clientVersion === undefined) {
      return;
    }
    if (latestClientVersion !== clientVersion) {
      setOpenRefreshModal(true);
      socket?.disconnect();
    }
  }, [latestClientVersion, clientVersion, socket]);

  const colorMode = React.useMemo(
    () => ({
      // The dark mode switch would invoke this method
      toggleColorMode: () => {
        setMode((prevMode: PaletteMode) =>
          prevMode === 'light' ? 'dark' : 'light'
        );
      },
    }),
    []
  );

  const checkMaintenanceStatus = async () => {
    const res = await fetchGet('/maintenance');
    if (res) setMaintenance(res);
  };

  const fetchClientVersion = async () => {
    const res = await fetchGet('/config/client-version');
    if (res) setClientVersion(res.clientVersion);
  };

  const reverseAllChat = (allChat: ChatMessage[]) =>
    setAllChat(allChat.reverse());

  const updateWhosOnline = (loggedInUser: WhosOnline) => {
    if (!loggedInUser) return;
    if (loggedInUser.profileId === profile.id) {
      selfWhosOnlineRef.current = loggedInUser;
      if (!loggedInUser.online) {
        setOpenRefreshModal(true);
        socket.disconnect();
      }
    }
    const replaceLoggedInUser = (
      loggedInUser: WhosOnline,
      users?: WhosOnline[]
    ) =>
      users
        ?.filter((u) => u.profileId !== loggedInUser.profileId)
        .concat(loggedInUser);
    setWhosOnline((prev) => replaceLoggedInUser(loggedInUser, prev));
  };

  const lobbyGameUpdate = (update: {
    gameId: string;
    game: Game;
    action: 'create' | 'update' | 'delete';
  }) => {
    console.log('lobbyGameUpdate', update.action);
    const replaceGame = (lobbyGames?: Game[]) =>
      lobbyGames
        ? lobbyGames.filter((g) => g.gameId !== update.game.gameId)
        : [];
    if (update.action === 'create' || update.action === 'update') {
      setAllLobbyGames((prev) => replaceGame(prev)?.concat(update.game));
      return;
    }
    if (update.action === 'delete') {
      const deleteGame = (lobbyGames?: Game[]) =>
        lobbyGames?.filter((g) => g.gameId !== update.gameId);
      setAllLobbyGames((prev) => deleteGame(prev));
      return;
    }
  };

  const lobbyPlayerUpdate = (data: {
    gameId: string;
    player: any;
    teams: Teams;
    action: 'addPlayer';
  }) => {
    if (data.action === 'addPlayer') {
      setAllLobbyGames((prev) => addPlayerToGame(data.player, prev));
      const addPlayerToGame = (player: any, lobbyGames?: Game[]) => {
        const found = lobbyGames?.find((g) => g.gameId === data.gameId);
        if (!found) return lobbyGames;
        found.activePlayers.push(player.profileId);
        if (found.mode === 'teams') {
          found.teams = data.teams;
        }
        found.players[player.profileId] = player;
        const updatedLobbyGames = lobbyGames
          ?.filter((g) => g.gameId !== data.gameId)
          .concat(found);
        return updatedLobbyGames;
      };
      return;
    }
  };

  const lobbyGamePropertyUpdate = (update: {
    gameId: string;
    property: keyof Game;
    data: any;
  }) => {
    setAllLobbyGames((prev) => updateGameProperty(update, prev));
    const updateGameProperty = (
      update: {
        gameId: string;
        property: keyof Game;
        data: any;
      },
      lobbyGames?: Game[]
    ) => {
      const found: any = lobbyGames?.find((g) => g.gameId === update.gameId);
      if (!found) return lobbyGames;
      found[update.property] = update.data;
      const updatedLobbyGames = lobbyGames
        ?.filter((g) => g.gameId !== update.gameId)
        .concat(found);
      return updatedLobbyGames;
    };
  };

  const updatePlayerIsReady = (player: Player) => {
    setPlayersReady((prev) => ({
      ...prev,
      [player.profileId]: player,
    }));
  };

  const fetchHistoricalGames = async (limit?: number) => {
    const games = await fetchGet(`/games/limit/${limit}`);
    setLobbyGamesHistory(games);
  };

  const fetchHistoricalTourneyGames = async (tournament: number) => {
    const games: Game[] = await fetchGet(`/games/tournament/${tournament}`);
    setTourneyLobbyGamesHistory(games);
  };

  const fetchHistoricalLeagueGames = async () => {
    const games: Game[] = await fetchGet(`/games/league/`);
    setLeagueLobbyGamesHistory(games);
  };

  const fetchDailyData = async () => {
    const dailyData = await fetchGet(`/games/daily-data`);
    setDailyData(dailyData);
    await fetchDailiesAuthor(dailyData.currentDailySet);
  };

  const fetchMobileDailyData = async () => {
    const mobileDailyData = await fetchGet(`/dailies/mobile-daily-data`);
    setMobileDailyData(mobileDailyData);
  };

  const fetchDailiesAuthor = async (dailySet: number) => {
    const res = await fetchGet(`/dailies/dailies-author/${dailySet}`);
    setDailiesAuthor(res);
  };

  const handleOnPlayClick = async () => {
    if (!profile || !socket) return;
    if (isMobile) {
      setOpenMobileDisclaimer(true);
      return;
    }
    if (profile.demoSetsSeen.length === 0 && !profile.demoSkipped) {
      setOpenLaunchPracticeGameModal(true);
      return;
    }
    if (onlinePlayer?.waitingConfirmReady) {
      socket.emit('requestConfirmReady', {
        gameId: onlinePlayer.currentGameId,
        profileId: onlinePlayer.profileId,
      });
      return;
    }
    if (onlinePlayer?.gameInProgress) {
      rejoinGame(socket, profile, rejoinGameSuccess, requestFailed);
      return;
    }
    if (!openPlayMenu) {
      setOpenPlayMenu(true);
    } else {
      if (!createGameConfig) return;
      const {
        allowSpectators,
        gameType,
        categorySelections,
        categorySet,
        mode,
        customSitAndGoMaxPlayers,
        matchMaking,
        ranked,
        demo,
        variant,
        soloMultiAsync,
        survivalMode,
        voluntarySeedMatch,
      } = createGameConfig;
      handleCreateGame(socket, profile, false, createGameConfig, false);
      await fetchPost('/profile/play-menu-settings', {
        profileId: profile?.id,
        gameType,
        categorySelections,
        categorySet,
        mode,
        soloMultiAsync,
        sitAndGoMaxPlayers: customSitAndGoMaxPlayers,
        ranked,
        matchMaking,
        allowSpectators,
        variant,
        survivalMode,
        voluntarySeedMatch,
      });
      if (demo) setDemoCount((prev) => prev + 1);
    }
  };

  const handleCreateGame = (
    socket: Socket,
    profile: Profile,
    isDaily: boolean,
    createGameConfig: CreateGameConfig,
    fromSoloPunchcard?: boolean,
    fromMain?: boolean,
    fromStarterKit?: boolean
  ) => {
    createGame(
      socket,
      profile,
      createGameSuccess,
      requestFailed,
      isDaily,
      createGameConfig,
      fromSoloPunchcard,
      fromMain,
      fromStarterKit
    );
  };

  const createGameSuccess = (
    gameDataFromServer: GameDataFromServer,
    fromSoloPunchcard?: boolean,
    fromMain?: boolean,
    fromStarterKit?: boolean
  ) => {
    const {
      daily,
      gameId,
      ghostChallenge,
      ghostChallengeFrom,
      ghostChallengeTo,
      matchMaking,
      matchMakingSeededBy,
      mode,
    } = gameDataFromServer;
    setGameId(gameId);
    setGameDataFromServer((prev) => ({
      ...prev,
      ...gameDataFromServer,
    }));
    if (mode === 'solo' || ghostChallenge || matchMaking) {
      setStartedGameDataFromServer((prev) => ({
        ...prev,
        ...gameDataFromServer,
      }));
      setOpenPlayMenu(false);
      if (ghostChallenge) {
        setOpenGhostChallenge(false);
        setOpenGhostChallengesView(false);
      }
      navigate('/room', {
        state: {
          daily,
          dailyTheme: dailyThemeRef.current,
          fromMain,
          fromSoloPunchcard,
          fromStarterKit,
          ghostChallenge,
          ghostChallengeFrom,
          ghostChallengeTo,
          matchMaking,
          matchMakingSeededBy,
        },
      });
    } else {
      if (mode === 'teams') {
        setOpenTeamPickingDialog(true);
      }
      setOpenPlayMenu(false);
      if (location.pathname !== '/lobby') navigate('/lobby');
    }
  };

  const handleCreateTourneyGame = (
    socket: Socket,
    profile: Profile,
    createGameConfig: CreateGameConfig
  ) => {
    createGame(
      socket,
      profile,
      createTourneyGameSuccess,
      requestFailed,
      false,
      createGameConfig
    );
  };

  const createTourneyGameSuccess = (gameDataFromServer: GameDataFromServer) => {
    const { daily, gameId, mode, tournament } = gameDataFromServer;
    setStartedGameDataFromServer((prev) => ({
      ...prev,
      ...gameDataFromServer,
    }));
    if (mode === 'solo') {
      setOpenPlayMenu(false);
      navigate('/room', {
        state: {
          daily,
          tournament,
        },
      });
    }
    if (location.pathname !== '/tournament') navigate('/tournament');
  };

  const joinGameSuccess = (gameDataFromServer: GameDataFromServer) => {
    setGameDataFromServer((prev) => ({
      ...prev,
      ...gameDataFromServer,
    }));
    const { gameId, mode } = gameDataFromServer;
    setGameId(gameId);
    if (mode === 'teams') {
      setOpenTeamPickingDialog(true);
    }
  };

  const rejoinGameSuccess = (gameDataFromServer: GameDataFromServer) => {
    const {
      daily,
      gameId,
      mode,
      tournament,
      ghostChallenge,
      ghostChallengeFrom,
      ghostChallengeTo,
      matchMaking,
      matchMakingSeededBy,
      team,
    } = gameDataFromServer;
    setStartedGameDataFromServer((prev) => ({
      ...prev,
      ...gameDataFromServer,
    }));
    setOpenPlayMenu(false);
    navigate('/room', {
      state: {
        rejoin: true,
        daily,
        tournament,
        ghostChallenge,
        ghostChallengeFrom,
        ghostChallengeTo,
        matchMaking,
        matchMakingSeededBy,
      },
    });
  };

  const spectateGameSuccess = (gameDataFromServer: GameDataFromServer) => {
    const { tournament } = gameDataFromServer;
    setSpectateGameDataFromServer(gameDataFromServer);
    setOpenPlayMenu(false);
    navigate('/room', {
      state: {
        spectate: true,
        tournament,
      },
    });
  };

  const handleJoinGame = (gameId: string, mode: Mode) => {
    if (!profile || !socket) return;
    joinGame(socket, profile, gameId, mode, joinGameSuccess, requestFailed);
  };

  const handleLeaveGame = (gameId: string, profileId: string) => {
    if (!profile || !socket) return;
    socket.emit('leaveGame', {
      gameId,
      profileId,
    });
  };

  const handleSpectateGame = (gameId: string) => {
    if (!profile) return;
    const _spectateSocket = io(
      devDockerLoopbackSpectateSocket(process.env.REACT_APP_SOCKET_SPECTATE),
      {
      path: '/spectate/socket.io',
      transports: ['websocket'],
      withCredentials: true,
    }
    );
    _spectateSocket.on('connect', () => {
      connectProfileToSocket(_spectateSocket, location.pathname, profile, isMobile);
      spectateGame(_spectateSocket, profile, gameId, spectateGameSuccess, requestFailed);
    });
    setSpectateSocket(_spectateSocket);
  };

  const requestFailed = (message?: string) => {
    if (message === 'Game in progress') {
      setCustomMessage({
        openModal: true,
        message: `You have a game in progress. Wait until it's over.`,
        title: 'Game in progress',
      });
    } else if (message === 'Already in game') {
      setCustomMessage({
        openModal: true,
        message: `You already joined a game. Go to the lobby and leave that game to start a new one`,
        title: 'Already joined game',
      });
    } else if (message === 'Player not found') {
      setErrorMessage('Please refresh this page');
      setOpenErrorMessageModal(true);
    } else if (message === 'Ghost challenge daily quota hit') {
      setCustomMessage({
        openModal: true,
        message: `You have sent the daily limit of 3 Ghost Challenges.`,
        title: 'Ghost Challenges Daily Limit',
      });
    } else if (message === 'Already sent this player a Ghost Challenge today') {
      setCustomMessage({
        openModal: true,
        message: `You have already sent this player a Ghost Challenge today.`,
        title: 'Ghost Challenges 1 Per Player',
      });
    } else {
      setCustomMessage({
        openModal: true,
        message,
        title: '',
      });
    }
  };

  const promptReadyCheck = (data: {
    players: Record<string, Player>;
    bannedCategories: string[];
    confirmReadyExpiresAt?: number;
    autoStartIn?: number;
  }) => {
    const { players, bannedCategories, confirmReadyExpiresAt, autoStartIn } =
      data;
    setBannedCategories(bannedCategories);
    setPlayersReady(players);
    setOpenConfirmReadyModal({
      open: true,
      username: undefined,
      confirmReadyExpiresAt,
      autoStartIn,
    });
    if (!socket || !profile) return;
    socket.emit('getOnlinePlayer', { profileId: profile.id });
  };

  const changeGameMode = (mode: Mode) => {
    setGameDataFromServer((prev: any) => ({ ...prev, mode }));
    setStartedGameDataFromServer((prev) => ({
      ...prev,
      mode,
    }));
  };

  const handleReadyClick = () => {
    if (!profile || !socket) return;
    socket.emit('confirmedReady', profile.id, (res: SocketCallback) => {
      if (res.status === 'ok') {
        setConfirmedReady(true);
      }
      if (res.status === 'failed') {
        setSocketCallbackResponse(res);
        setOpenConfirmReadyModal({ open: false, username: undefined });
      }
    });
  };

  const handleReadyCancelClick = (username: string) => {
    if (!profile || !socket) return;
    socket.emit('confirmedNotReady', profile.id, (res: SocketCallback) => {
      if (res.status === 'ok') {
        setOpenConfirmReadyModal({ open: false, username });
        setConfirmedReady(false);
      }
      if (res.status === 'failed') {
        setSocketCallbackResponse(res);
      }
    });
  };

  const handleReadyExpired = () => {
    if (!profile || !socket) return;
    socket.emit('confirmReadyExpired', profile.id);
    setOpenConfirmReadyModal({ open: false });
    setConfirmedReady(false);
  };

  const handleReadyCancelByOtherPlayer = (username: string) => {
    setOpenSnackbar({
      open: true,
      message: `${username} is not joining the game`,
    });
    setOpenConfirmReadyModal({ open: false, username, delayedClose: true });
    if (!socket || !profile) return;
    socket.emit('getOnlinePlayer', { profileId: profile.id });
    setConfirmedReady(false);
  };

  const handleLogoutClick = async () => {
    logoutClickedRef.current = true;
    if (onlinePlayer?.currentGameId && profile) {
      socket?.emit('leaveGame', {
        gameId,
        profileId: profile.id,
      });
    }
    await track(profile?.id || null, 'header.logout.link');
    const res = await fetchPost('/authenticate/logout');
    console.log('logout res', res);
    navigate('/login');
    setProfile(undefined);
  };

  const onUpdateProfile = async (updatedUsername?: string) => {
    const res = await fetchGet('/profile');
    setProfile(res as Profile);
    if (updatedUsername && socket && profile) {
      socket.emit('updatedUsername', {
        profileId: profile.id,
        username: updatedUsername,
      });
    }
  };

  const handlePlayPracticeGameClick = () => {
    if (!profile || !socket) return;
    if (isMobile) {
      setOpenMobileDisclaimer(true);
      return;
    }
    const practiceGameOptions: CreateGameConfig = {
      allowSpectators: false,
      demo: true,
      customGameName: `${profile?.username} Demo #${demoCount | 0}`,
      gameType: 'casual',
      categorySet: 'all',
      mode: 'solo',
      ranked: false,
      starterKit: null,
    };
    setCreateGameConfig(practiceGameOptions);
    handleCreateGame(socket, profile, false, practiceGameOptions, false, true);
    setOpenLaunchPracticeGameModal(false);
    setOpenRecommendPractice(false);
  };

  const handleSkipPracticeGameClick = async () => {
    await fetchPost('/profile/demo-skipped', profile);
    setProfile((prev: any) => ({ ...prev, demoSkipped: true }));
    setOpenLaunchPracticeGameModal(false);
    setOpenPlayMenu(true);
  };

  const handlePlayDailyClick = (
    dailySet: number,
    dailyTheme?: string,
    missedDaily?: boolean
  ) => {
    if (isMobile) {
      setOpenMobileDisclaimer(true);
      return;
    }
    if (profile.demoSetsSeen.length === 0 && !profile.demoSkipped) {
      setOpenRecommendPractice(true);
      return;
    }
    dailyThemeRef.current = dailyTheme;
    const obj = {
      open: true,
      dailySet,
      missedDaily,
      starterKit: null,
    };
    setOpenPlayDailyConfirmation(obj);
  };

  const handlePlayDailyConfirm = (dailySet?: number, missedDaily?: boolean) => {
    if (!profile || !socket || !dailySet) return;
    setOpenRecommendPractice(false);
    setOpenPlayDailyConfirmation({ open: false });
    if (!missedDaily) setLastDailySetPlayedCache(dailySet);
    const dailyGameOptions: CreateGameConfig = {
      daily: true,
      dailySet: dailySet,
      missedDaily: missedDaily,
    };
    setCreateGameConfig(dailyGameOptions);
    handleCreateGame(socket, profile, true, dailyGameOptions);
  };

  const handleDemonButtonClick = () => {
    if (isMobile) {
      setOpenMobileDisclaimer(true);
      return;
    }
    setOpenFaceYourDemons(true);
  };

  const handlePlayDemonsClick = () => {
    if (!profile || !socket) return;
    setOpenRecommendPractice(false);
    setOpenFaceYourDemons(false);
    const updateCreateGameConfig: CreateGameConfig = {
      ...createGameConfig,
      allowSpectators: profile?.demons?.allowSpectators,
      categorySet: 'all',
      demons: true,
      gameType: 'pro',
      mode: 'solo',
      matchMaking: false,
      ranked: false,
      starterKit: null,
      survivalMode: 'demon',
      variant: 'survival',
    };
    handleCreateGame(socket, profile, false, updateCreateGameConfig, false);
  };

  const handleDailyCountdownStop = () => fetchDailyData();

  const onTabFocus = () => {
    debouncedTabFocus();
  };

  const debouncedTabFocus = debounce(() => {
    socket?.emit('input-f', usernameRef.current);
  }, 200);

  const onTabBlur = () => {
    debouncedTabBlur();
  };

  const debouncedTabBlur = debounce(() => {
    socket?.emit('input-b', usernameRef.current);
  }, 200);

  const onIdle = () => {
    socket?.emit('idleUser', { profileId: profile?.id, isIdle: true });
  };

  const onActive = () => {
    socket?.emit('idleUser', { profileId: profile?.id, isIdle: false });
  };

  const onAction = () => {
    if (!profile) return;
    const self = whosOnline?.find((p) => p.profileId === profile.id);
    if (self && self.isIdle) {
      socket?.emit('idleUser', { profileId: profile?.id, isIdle: false });
    }
  };

  const updateFlashcards = (flashcards: Flashcard[]) => {
    setFlashcards(flashcards);
  };

  useIdleTimer({
    onIdle,
    onActive,
    onAction,
    timeout: 300_000,
    throttle: 500,
  });

  const getDesignTokens = (mode: PaletteMode) => ({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            // palette values for light mode
            ...paletteLight,
          }
        : {
            // palette values for dark mode
            ...palette,
          }),
    },
    typography: {
      button: {
        fontWeight: 600,
      },
      fontFamily: ['IBM Plex Mono', 'monospace'].join(','),
    },
  });

  const theme = React.useMemo(
    () => responsiveFontSizes(createTheme(getDesignTokens(mode))),
    [mode]
  );

  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  const handleGhostChallenge = (player: {
    profileId: string;
    username: string;
    ghostChallengeStatus: string;
  }) => {
    setGhostChallengeTo(player);
    setOpenGhostChallenge(true);
  };

  const submitPlayGhostChallenge = async (config: GhostChallengConfig) => {
    if (!profile || !socket) return;
    if (isMobile) {
      setOpenMobileDisclaimer(true);
      return;
    }
    const createGameConfig: CreateGameConfig = {
      ...config,
      demo: false,
      mode: 'match',
    };
    setCreateGameConfig(createGameConfig);
    handleCreateGame(socket, profile, false, createGameConfig);
    setOpenLaunchPracticeGameModal(false);
    const { categorySet, gameType, ranked } = createGameConfig;
    await fetchPost('/profile/ghost-challenge-settings', {
      profileId: profile?.id,
      categorySet,
      difficulty: gameType,
      ranked,
    });
  };

  const handleGhostChallengesView = () => {
    setOpenGhostChallengesView(true);
  };

  const handlePlayGhostMatchClick = (
    gameId: string,
    ghostChallengeFrom: string,
    ghostChallengeTo: string
  ) => {
    if (!profile || !socket) return;
    playGhostChallenge(
      socket,
      profile,
      gameId,
      ghostChallengeFrom,
      ghostChallengeTo,
      createGameSuccess,
      requestFailed
    );
  };

  const fetchGhostChallengesReceived = async (profileId: string) => {
    const res: GhostChallenge[] = await fetchGet(
      `/games/ghost-challenges/received-sent/received/${profileId}`
    );
    const incomingGhostChallenges = res.filter(
      (gc) =>
        !gc.ghostChallengeData.completedAt && !gc.ghostChallengeData.expired
    ).length;
    setIncomingGhostChallenges(incomingGhostChallenges);
  };

  const updateProfileCompletedTour = (completedTour: boolean) => {
    setProfile((prev: any) => ({ ...prev, completedTour: completedTour }));
  };

  const onCategoryBanClick = () => {
    setOpenCategoryBans(true);
  };

  const onPlayFlashcardDeck = (deckId: string) => {
    if (!profile || !socket) return;
    const createGameConfig: CreateGameConfig = {
      customGameName: 'flashcard play',
      flashcardDeck: true,
      flashcardDeckId: deckId,
      gameType: 'pro',
      categorySet: 'all',
      mode: 'solo',
      ranked: false,
      starterKit: null,
    };
    setCreateGameConfig(createGameConfig);
    handleCreateGame(socket, profile, false, createGameConfig, true);
  };

  const onPlaySoloFromPunchcard = (
    gameType: 'casual' | 'pro',
    categorySet: string,
    customGameName?: string
  ) => {
    if (!profile || !socket) return;
    const createGameConfig: CreateGameConfig = {
      allowSpectators: profile?.recentPlayMenuSettings?.allowSpectators,
      customGameName,
      gameType,
      categorySet,
      mode: 'solo',
      ranked: false,
      starterKit: null,
      demons: false,
    };
    setCreateGameConfig(createGameConfig);
    handleCreateGame(socket, profile, false, createGameConfig, true);
  };

  const onTourneyGameCreate = (createGameConfig: CreateGameConfig) => {
    if (!profile || !socket) return;
    setCreateGameConfig(createGameConfig);
    handleCreateTourneyGame(socket, profile, createGameConfig);
  };

  const clearGameDataFromServer = () => setGameDataFromServer(undefined);

  const showSnackbarMessage = (message: string) => {
    setOpenSnackbar({
      open: true,
      message,
    });
  };

  const onViewSearchPoolClick = () => {
    setOpenSearchPoolDialog(true);
  };

  const onViewHandicapWinRatesClick = () => {
    setOpenHandicapWinrates(true);
  };

  const fetchDemons = async () => {
    const res = await fetchGet(`/demons/user/${profile.id}`);
    setDemonsData(res);
  };

  const fetchUserAchievements = async () => {
    const res = await fetchGet(`/achievements/user/${profile?.id}`);
    if (!res) return;
    setDetailedUserAchievements(res);
  };

  const handleQuickPlay = (isMatchMaking?: boolean) => {
    if (isMobile) {
      setOpenMobileDisclaimer(true);
      return;
    }
    const updatedCreateGameConfig: CreateGameConfig = {
      ...createGameConfig,
      matchMaking: isMatchMaking,
    };
    setCreateGameConfig(createGameConfig);
    handleCreateGame(
      socket,
      profile,
      false,
      updatedCreateGameConfig,
      false,
      true
    );
  };

  const handleStarterKitPlay = () => {
    if (isMobile) {
      setOpenMobileDisclaimer(true);
      return;
    }
    handleCreateGame(
      socket,
      profile,
      false,
      createGameConfig,
      false,
      false,
      true
    );
  };

  const disablePlayButton = () => {
    if (maintenance?.scheduled) return true;
    if (!socket?.connected) return true;
    if (!openPlayMenu) return false;
    if (!createGameConfig) return false;
    const { soloMultiAsync, variant, categorySelections, categorySet, ranked } =
      createGameConfig;
    if (
      !socket.connected ||
      (soloMultiAsync === 'solo' &&
        variant === 'normal' &&
        !categorySelections?.length &&
        categorySet === 'none')
    ) {
      return true;
    }
    if (
      soloMultiAsync === 'multiplayer' &&
      !ranked &&
      !categorySelections?.length &&
      categorySet === 'none'
    ) {
      return true;
    }
    if (soloMultiAsync === 'teams' && !enabledTeams) {
      return true;
    }
    return false;
  };

  const handleRejoinGameClick = (gameId: string) => {
    rejoinGame(socket, profile, rejoinGameSuccess, requestFailed);
  };

  const handlePickTeamsClick = (gameId: string) => {
    socket.emit('requestTeamPickingStatus', {
      gameId,
      profileId: profile.id,
    });
    setOpenTeamPickingDialog(true);
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider
        theme={{ ...theme, components: muiCustomComponent(theme) }}
      >
        {profile && socket && gameId && teamPickingStatus ? (
          <TeamPickingDialog
            admin={profile.admin}
            close={() => setOpenTeamPickingDialog(false)}
            open={openTeamPickingDialog}
            gameId={gameId}
            profileId={profile.id}
            teamPickingStatus={teamPickingStatus}
            socket={socket}
            username={profile.username}
          />
        ) : null}
        {profile && location.pathname === '/' ? (
          <JoyrideComponent
            profile={profile}
            updateProfileCompletedTour={updateProfileCompletedTour}
          ></JoyrideComponent>
        ) : null}
        <MobileDisclaimer
          closeMobileDisclaimer={() => setOpenMobileDisclaimer(false)}
          openMobileDisclaimer={openMobileDisclaimer}
        ></MobileDisclaimer>
        {profile ? (
          <SkillAssessmentDialog
            close={() => setOpenSkillAssessment(false)}
            open={openSkillAssessment}
            profileId={profile.id}
          ></SkillAssessmentDialog>
        ) : null}
        {profile ? (
          <TournamentSignUpDialog
            close={() => setOpenTourneySignUp(false)}
            open={openTourneySignUp}
            profileId={profile.id}
          ></TournamentSignUpDialog>
        ) : null}
        {profile && ghostChallengeTo ? (
          <GhostChallengeDialog
            close={() => setOpenGhostChallenge(false)}
            ghostChallengesSentToToday={profile.ghostChallengesSentToToday}
            ghostChallengeSettings={profile.ghostChallengeSettings}
            ghostChallengeTo={ghostChallengeTo}
            isAdmin={profile.admin}
            lastGhostChallengeSentAt={profile.lastGhostChallengeSentAt}
            maintenance={maintenance}
            open={openGhostChallenge}
            profileId={profile.id}
            submitPlayGhostChallenge={submitPlayGhostChallenge}
          ></GhostChallengeDialog>
        ) : null}
        {profile ? (
          <GhostChallengesViewDialog
            close={() => setOpenGhostChallengesView(false)}
            ghostChallengeStatus={profile.ghostChallengeStatus}
            handleGhostChallenge={handleGhostChallenge}
            handlePlayGhostMatchClick={handlePlayGhostMatchClick}
            isAdmin={profile.admin}
            maintenance={maintenance}
            onUpdateProfile={onUpdateProfile}
            open={openGhostChallengesView}
            profile={profile}
          ></GhostChallengesViewDialog>
        ) : null}
        <ConfirmLogoutDialog
          close={() => setOpenConfirmLogoutDialog(false)}
          handleConfirmLogout={handleLogoutClick}
          open={openConfirmLogoutDialog}
        />
        <PlayDailyConfirmationDialog
          open={openPlayDailyConfirmation}
          handlePlayDailyConfirm={handlePlayDailyConfirm}
          close={() => setOpenPlayDailyConfirmation({ open: false })}
        />
        {profile ? (
          <FaceYourDemonsDialog
            allowSpectators={profile?.demons?.allowSpectators}
            bannedDemon={profile?.demons?.bannedDemon}
            close={() => setOpenFaceYourDemons(false)}
            demonGamePlayedToday={profile?.demons?.demonGamePlayedToday}
            demonsData={demonsData}
            fetchDemonsData={fetchDemons}
            handlePlayDemonsClick={handlePlayDemonsClick}
            maintenance={maintenance}
            onUpdateProfile={onUpdateProfile}
            open={openFaceYourDemons}
            profileId={profile.id}
            totalDemonGamesPlayed={profile.totalDailyGamesPlayed}
          />
        ) : null}
        {profile ? (
          <CategoryBansDialog
            bannedCategories={profile.bannedCategories}
            close={() => setOpenCategoryBans(false)}
            onUpdateProfile={onUpdateProfile}
            open={openCategoryBans}
            profileId={profile.id}
          ></CategoryBansDialog>
        ) : null}
        <CssBaseline />
        <Header
          bannerMessage={bannerMessage}
          connected={socket?.connected}
          disablePlayButton={disablePlayButton()}
          soloMultiAsync={createGameConfig?.soloMultiAsync}
          handleLogoutClick={() => setOpenConfirmLogoutDialog(true)}
          isAdmin={isAdmin}
          isLoggedIn={!!profile}
          lobbyGames={lobbyGames}
          leagueLobbyGames={leagueLobbyGames}
          tournamentLobbyGames={tournamentLobbyGames}
          maintenance={maintenance}
          onlinePlayer={onlinePlayer}
          onPlayClick={handleOnPlayClick}
          onTourneyGameCreate={onTourneyGameCreate}
          onUpdateProfile={onUpdateProfile}
          openPlayMenu={openPlayMenu}
          profile={profile}
          showMultipleTabsBanner={showMultipleTabsBanner}
          voluntarySeedMatch={createGameConfig?.voluntarySeedMatch}
        />
        {profile ? (
          <PlayMenu2
            connected={socket?.connected}
            closePlayMenu={() => setOpenPlayMenu(false)}
            demoCount={demoCount}
            disablePlayButton={disablePlayButton()}
            enabledTeams={enabledTeams}
            onCategoryBanClick={onCategoryBanClick}
            onlinePlayer={onlinePlayer}
            onPlayClick={handleOnPlayClick}
            onViewHandicapWinRatesClick={onViewHandicapWinRatesClick}
            onViewSearchPoolClick={onViewSearchPoolClick}
            openPlayMenu={openPlayMenu}
            profile={profile}
            setCreateGameConfig={setCreateGameConfig}
          />
        ) : null}
        {profile ? (
          <ConfirmReadyModal
            bannedCategories={bannedCategories}
            confirmedReady={confirmedReady}
            handleReadyCancelClick={handleReadyCancelClick}
            handleReadyClick={handleReadyClick}
            handleReadyExpired={handleReadyExpired}
            mode={gameDataFromServer?.mode}
            openConfirmReadyModal={openConfirmReadyModal}
            players={playersReady}
            profile={profile}
            ranked={gameDataFromServer?.ranked}
            tournament={gameDataFromServer?.tournament}
          ></ConfirmReadyModal>
        ) : null}
        {profile ? (
          <ViewSearchPoolDialog
            close={() => setOpenSearchPoolDialog(false)}
            open={openSearchPoolDialog}
            profileId={profile.id}
            username={profile.username}
          />
        ) : null}
        {profile ? (
          <HandicapWinratesDialog
            close={() => setOpenHandicapWinrates(false)}
            open={openHandicapWinrates}
            profileId={profile.id}
          />
        ) : null}
        <CustomMessageModal
          customMessage={customMessage}
          handleCustomMessageSubmit={() =>
            setCustomMessage({ openModal: false })
          }
          setCustomMessage={setCustomMessage}
        ></CustomMessageModal>
        <ErrorMessageModal
          errorMessage={errorMessage}
          openErrorMessageModal={openErrorMessageModal}
          setOpenErrorMessageModal={setOpenErrorMessageModal}
        ></ErrorMessageModal>
        <LaunchPracticeGameModal
          handlePlayPracticeGameClick={handlePlayPracticeGameClick}
          handleSkipPracticeGameClick={handleSkipPracticeGameClick}
          openLaunchPracticeGameModal={openLaunchPracticeGameModal}
        ></LaunchPracticeGameModal>
        <RecommendPracticeGameDialog
          close={() => setOpenRecommendPractice(false)}
          handlePlayPracticeGameClick={handlePlayPracticeGameClick}
          open={openRecommendPractice}
        ></RecommendPracticeGameDialog>
        {/* <DisconnectedModal
          open={
            openDisconnectedModal &&
            location.pathname !== '/login' &&
            location.pathname !== '/signup'
          }
        ></DisconnectedModal> */}
        <RefreshBrowserDialog open={openRefreshModal} />
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openSnackbar?.open}
          autoHideDuration={50000}
          onClose={() => setOpenSnackbar({ open: false, message: '' })}
          message={openSnackbar?.message}
        />
        <Routes>
          <>
            <Route
              path="login"
              element={
                profile ? <Navigate to="/" /> : <Auth setProfile={setProfile} />
              }
            />
            <Route
              path="signup"
              element={
                profile ? <Navigate to="/" /> : <Auth setProfile={setProfile} />
              }
            />
            <Route path="unsubscribe/:token" element={<Unsubscribe />} />
            <Route path="/replay/:gameId" element={<ReplayPage />} />
            <Route
              path="/"
              element={
                profile && dailyData ? (
                  <Main
                    connected={socket?.connected}
                    dailiesAuthor={dailiesAuthor}
                    dailyData={dailyData}
                    demonsData={demonsData}
                    demonGamePlayedToday={profile?.demons?.demonGamePlayedToday}
                    handleDailyCountdownStop={handleDailyCountdownStop}
                    handleDemonButtonClick={handleDemonButtonClick}
                    handleGhostChallengesView={handleGhostChallengesView}
                    handlePlayDailyClick={handlePlayDailyClick}
                    handlePlayPracticeGameClick={handlePlayPracticeGameClick}
                    handleQuickPlay={handleQuickPlay}
                    incomingGhostChallenges={incomingGhostChallenges}
                    lastDailySetPlayedCache={lastDailySetPlayedCache}
                    lobbyGames={lobbyGames}
                    maintenance={maintenance}
                    mobileDailyData={mobileDailyData}
                    onlinePlayer={onlinePlayer}
                    onUpdateProfile={onUpdateProfile}
                    profile={profile}
                    setCreateGameConfig={setCreateGameConfig}
                    testingFeature={testingFeature}
                  />
                ) : null
              }
            />
            <Route
              path="/lobby"
              element={
                profile ? (
                  <Lobby
                    clearGameDataFromServer={clearGameDataFromServer}
                    gameDataFromServer={gameDataFromServer}
                    handleJoinGameClick={handleJoinGame}
                    handleLeaveGameClick={handleLeaveGame}
                    handlePickTeamsClick={handlePickTeamsClick}
                    handleRejoinGameClick={handleRejoinGameClick}
                    handleSpectateClick={handleSpectateGame}
                    lobbyGames={lobbyGames}
                    lobbyGamesHistory={lobbyGamesHistory}
                    onlinePlayer={onlinePlayer}
                    openTeamPickingDialog={openTeamPickingDialog}
                    profile={profile}
                    socket={socket}
                  />
                ) : null
              }
            />
            <Route
              path="/tournament"
              element={
                profile && socket ? (
                  <TournamentLobby
                    handleJoinGameClick={handleJoinGame}
                    handleSpectateClick={handleSpectateGame}
                    lobbyGames={tournamentLobbyGames}
                    lobbyGamesHistory={tourneyLobbyGamesHistory}
                    onTourneyGameCreate={onTourneyGameCreate}
                    profile={profile}
                    socket={socket}
                  />
                ) : null
              }
            />
            {startedGameDataFromServer || spectateGameDataFromServer ? (
              <Route
                path="loading"
                element={
                  profile ? (
                    <GameLoading
                      daily={startedGameDataFromServer?.daily}
                      tournament={startedGameDataFromServer?.tournament}
                    />
                  ) : null
                }
              />
            ) : null}
            {startedGameDataFromServer || spectateGameDataFromServer ? (
              <Route
                path="room"
                element={
                  profile ? (
                    <Room
                      detailedUserAchievements={detailedUserAchievements}
                      profile={profile}
                      gameDataFromServer={startedGameDataFromServer}
                      handleJoinGameClick={handleJoinGame}
                      handleLeaveGameClick={handleLeaveGame}
                      onCategoryBanClick={onCategoryBanClick}
                      lobbyOpenGames={lobbyOpenGames}
                      onUpdateProfile={onUpdateProfile}
                      showSnackbarMessage={showSnackbarMessage}
                      socket={socket}
                      spectateSocket={spectateSocket}
                      spectateGameDataFromServer={spectateGameDataFromServer}
                      whosOnline={whosOnline}
                    />
                  ) : null
                }
              />
            ) : null}
            <Route
              path="pool"
              element={
                profile && socket ? (
                  <PoolPlay
                    handleSocketCallback={setSocketCallbackResponse}
                    profile={profile}
                    setGameId={setGameId}
                    socket={socket}
                  />
                ) : null
              }
            />
            <Route
              path="admin/inaccuracies-report"
              element={
                profile ? <InaccuraciesReport profile={profile} /> : null
              }
            />
            <Route
              path="admin/dashboard"
              element={
                socket ? (
                  <Admin
                    enabledTeams={enabledTeams}
                    maintenance={maintenance}
                    profileId={profile?.id}
                    socket={socket}
                    setTestingFeature={setTestingFeature}
                    testingFeature={testingFeature}
                  />
                ) : null
              }
            />
            <Route path="admin/questions-setup" element={<QuestionsSetup />} />
            <Route
              path="verify/:token"
              element={<Auth setProfile={setProfile} />}
            />
            <Route
              path="reset-password/:token"
              element={<Auth setProfile={setProfile} />}
            />
            <Route
              path="forgot-password"
              element={<Auth setProfile={setProfile} />}
            />
            <Route
              path="profile/overview/:profileId"
              element={
                profile ? (
                  <ProfileMain
                    handleGhostChallenge={handleGhostChallenge}
                    onUpdateProfile={onUpdateProfile}
                    profile={profile}
                  />
                ) : null
              }
            />
            <Route
              path="profile/achievements/:profileId"
              element={<ProfileAchievements />}
            />
            <Route
              path="profile/punchcard/:profileId"
              element={
                profile ? (
                  <SoloPunchcard
                    maintenance={maintenance}
                    onPlaySoloFromPunchcard={onPlaySoloFromPunchcard}
                    myProfileId={profile.id}
                  />
                ) : null
              }
            />
            <Route
              path="profile/advanced-stats/:profileId"
              element={<ProfileAdvancedStats />}
            />
            <Route
              path="profile/custom-questions/:profileId"
              element={profile ? <CQDashboard profile={profile} /> : null}
            />
            <Route
              path="profile/game-settings/:profileId"
              element={
                profile ? (
                  <ProfileSettings
                    onCategoryBanClick={onCategoryBanClick}
                    onUpdateProfile={onUpdateProfile}
                    profile={profile}
                  />
                ) : null
              }
            />
            <Route
              path="profile/account/:profileId"
              element={
                profile ? (
                  <ProfileAccount
                    onUpdateProfile={onUpdateProfile}
                    profile={profile}
                  />
                ) : null
              }
            />
            <Route
              path="explore/leaderboards"
              element={<Leaderboards isAdmin={profile?.admin} />}
            />
            <Route
              path="explore/starter-kits"
              element={
                !!profile ? (
                  <StarterKits
                    handleStarterKitPlay={handleStarterKitPlay}
                    maintenance={maintenance}
                    profile={profile}
                    setCreateGameConfig={setCreateGameConfig}
                  />
                ) : null
              }
            />
            <Route
              path="/league/lounge"
              element={
                profile && socket ? (
                  <League
                    handleJoinGameClick={handleJoinGame}
                    handleLeaveGameClick={handleLeaveGame}
                    handlePickTeamsClick={handlePickTeamsClick}
                    handleRejoinGameClick={handleRejoinGameClick}
                    handleSpectateClick={handleSpectateGame}
                    onTourneyGameCreate={onTourneyGameCreate}
                    lobbyGames={leagueLobbyGames}
                    lobbyGamesHistory={leagueLobbyGamesHistory}
                    profile={profile}
                    socket={socket}
                  />
                ) : null
              }
            />
            <Route
              path="/league/teams"
              element={profile ? <LeagueTeams /> : null}
            />
            <Route
              path="/league/standings"
              element={profile ? <LeagueStandings /> : null}
            />
            <Route
              path="/league/players"
              element={profile ? <LeaguePlayers /> : null}
            />
            <Route
              path="/league/rules"
              element={profile ? <LeagueRules /> : null}
            />
            <Route
              path="/league/join"
              element={profile ? <LeagueHowToJoin /> : null}
            />
            <Route
              path="/league/manage"
              element={profile ? <LeagueAdmin profileId={profile.id} /> : null}
            />
            <Route
              path="pricing"
              element={<PricingPage isAdmin={profile?.admin} />}
            />
            <Route path="game/view/:gameId" element={<ScoreScreenPublic />} />
            <Route
              path="explore/dailies"
              element={
                !!profile && dailyData ? (
                  <Dailies
                    connected={socket?.connected}
                    dailiesAuthor={dailiesAuthor}
                    dailyData={dailyData}
                    handleDailyCountdownStop={handleDailyCountdownStop}
                    handlePlayDailyClick={handlePlayDailyClick}
                    maintenance={maintenance}
                    lastDailySetPlayedCache={lastDailySetPlayedCache}
                    profile={profile}
                  />
                ) : null
              }
            />
            <Route
              path="admin/flashcards"
              element={
                !!profile ? (
                  <Flashcards
                    flashcards={flashcards}
                    profile={profile}
                    updateFlashcards={updateFlashcards}
                  />
                ) : null
              }
            />
            <Route
              path="study"
              element={<FlashcardMode flashcards={flashcards} />}
            />
            <Route path="help" element={<Help />} />
            <Route path="stager" element={<Stager />} />
            <Route
              path="explore/submit-question"
              element={<CustomQuestions profile={profile} />}
            />
            <Route
              path="admin/review-questions"
              element={
                profile ? <UserSubmittedQuestions profile={profile} /> : null
              }
            />
            <Route path="stats" element={<Stats />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        </Routes>
        {profile &&
        !openAllChat &&
        unreadAllChat &&
        !location.pathname.includes('/room') &&
        location.pathname !== '/study' ? (
          <Fade in={showAllChatPreview}>
            <Typography
              textAlign={'center'}
              sx={{
                position: 'fixed',
                maxWidth: '500px',
                bottom: 30,
                color: theme.palette.primary.main,
                border: '1px solid',
                borderColor: theme.palette.dailies.main,
                borderRadius: '.25rem',
                backgroundColor: '#2b2b2b',
                overflow: 'hidden',
                maxHeight: '42px',
                opactiy: 0.75,
                transform: 'translate(-50%, -50%)',
                left: '50%',
                fontSize: 20,
                paddingTop: '5px',
                paddingBottom: '5px',
                paddingLeft: 2,
                paddingRight: 2,
              }}
            >
              <span
                style={{
                  marginRight: 10,
                  textAlign: 'left',
                  color: 'white',
                  minWidth: '50px',
                  maxWidth: '120px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'pre',
                  fontWeight: 600,
                }}
              >
                {allChat && allChat[allChat.length - 1].username}:
              </span>
              {allChat && allChat[allChat.length - 1].message}
            </Typography>
          </Fade>
        ) : null}
        {profile &&
        socket &&
        !location.pathname.includes('/room') &&
        location.pathname !== '/study' ? (
          <>
            <BottomNav
              allChat={allChat}
              incomingGhostChallenges={incomingGhostChallenges}
              openAllChat={openAllChat}
              playersOnlineCount={playersOnlineCount}
              profile={profile}
              whosOnline={whosOnline}
              socket={socket}
              handleGhostChallenge={handleGhostChallenge}
              handleGhostChallengesView={handleGhostChallengesView}
              setOpenAllChat={setOpenAllChat}
              unreadAllChat={unreadAllChat}
            />
          </>
        ) : null}
        <SocketCallbackHandler
          socketCallbackResponse={socketCallbackResponse}
        ></SocketCallbackHandler>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
